// src/components/TeamItemsPage/TeamItemsPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import TeamItemCard from './TeamItemCard';
import TeamItemCardSkeleton from './TeamItemCardSkeleton';
import TeamItemModal from './TeamItemModal';
import TeamItemTable from './TeamItemTable';
import KanbanView from './KanbanView';
import QuickViewPanel from './QuickViewPanel';
import TaskTemplatesModal from './TaskTemplatesModal';
import CalendarView from './CalendarView';
import styles from './TeamItemsPage.module.css';
import { useParams, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { canManageProgram } from '../../hooks/useProgramsApi';
import { 
  FaThLarge, FaTable, FaTh, FaCalendar, FaSearch, FaFilter, FaSort, 
  FaCheckSquare, FaTrash, FaEdit, FaStar, FaTimes, FaClipboardList,
  FaExclamationTriangle, FaClock, FaSpinner, FaCheckCircle, FaPlay,
  FaPlus, FaLayerGroup, FaUser
} from 'react-icons/fa';
import { isBefore, startOfDay, differenceInCalendarDays } from 'date-fns';
import { auth } from '../../firebaseConfig';
import confetti from 'canvas-confetti';

const TeamItemsPage = () => {
  const { programId } = useParams();
  const location = useLocation();
  const { userClubs, userProfile } = useUser();
  
  // Get itemId from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const highlightItemId = urlParams.get('itemId');
  const [teamItems, setTeamItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

  // Program permissions state
  const [programPermissions, setProgramPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [resolvedClubId, setResolvedClubId] = useState(null);

  // View mode state (grid, table, kanban, calendar)
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('teamItemsViewMode') || 'grid';
  });

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterDueDate, setFilterDueDate] = useState('all');
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showMyAssignments, setShowMyAssignments] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Bulk actions state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkActionMode, setBulkActionMode] = useState(false);

  // Quick view panel state
  const [quickViewItem, setQuickViewItem] = useState(null);

  // Comments state (now from database)
  const [itemComments, setItemComments] = useState({});

  // Authorization logic - similar to EventsPage
  const canManage = useMemo(() => {
    // Check if user has club management role
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(resolvedClubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      return isTargetClub && isOwnerOrManager;
    });

    // Check if user has program management permissions
    const hasProgramPermissions = canManageProgram(programPermissions);

    return hasClubRole || hasProgramPermissions;
  }, [userClubs, resolvedClubId, programPermissions]);

  // Fetch program details to get clubId
  useEffect(() => {
    if (programId) {
      const fetchProgramDetails = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`
          );
          const program = response.data;
          
          // Extract clubId correctly (handle both string and object cases)
          const extractedClubId =
            program.clubId && typeof program.clubId === 'object'
              ? program.clubId._id
              : program.clubId;
              
          setResolvedClubId(extractedClubId);
        } catch (err) {
          console.error('Error fetching program details:', err);
          
          // Fallback: Try to find matching club from user's memberships
          if (Array.isArray(userClubs) && userClubs.length > 0) {
            // Since we can't get the program details, we'll try to find a club where user is a manager
            const managerClub = userClubs.find(m => {
              const role = (m.role || '').toLowerCase();
              return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
            });
            
            if (managerClub) {
              const fallbackClubId = managerClub.club?._id || managerClub.club;
              setResolvedClubId(fallbackClubId);
            }
          }
        }
      };
      
      fetchProgramDetails();
    }
  }, [programId, userClubs]);

  // Auto-scroll to highlighted team item and open quick view when page loads
  useEffect(() => {
    if (highlightItemId && teamItems.length > 0) {
      // Find the item to open in quick view
      const itemToHighlight = teamItems.find(item => item._id === highlightItemId);
      
      // Small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        // Scroll to the item
        const itemElement = document.querySelector(`[data-item-id="${highlightItemId}"]`);
        if (itemElement) {
          itemElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }
        
        // Open quick view panel for the highlighted item
        if (itemToHighlight) {
          setQuickViewItem(itemToHighlight);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [highlightItemId, teamItems]);

  // Fetch program permissions for current user
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/permissions`);
        if (response.ok) {
          const data = await response.json();
          setProgramPermissions(data?.permissions || []);
        } else {
          setProgramPermissions([]);
        }
      } catch (error) {
        console.error('Error fetching program permissions:', error);
        setProgramPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchProgramPermissions();
  }, [programId]);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('teamItemsViewMode', viewMode);
  }, [viewMode]);

  // Fetch team items
  useEffect(() => {
    const fetchTeamItems = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/team-items`,
          {
            params: { programId },
           
          }
        );
        setTeamItems(response.data.teamItems);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching team items:', err);
        setError(true);
        setLoading(false);
      }
    };

    if (programId) {
      fetchTeamItems();
    } else {
      setError(true);
      setLoading(false);
    }
  }, [programId]);

  // Fetch comments for an item
  const fetchComments = useCallback(async (itemId) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemId}/comments`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const comments = await response.json();
        setItemComments(prev => ({
          ...prev,
          [itemId]: comments
        }));
        return comments;
      } else {
        console.error('Failed to fetch comments:', response.status);
        return [];
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  }, []);

  // Add comment to an item (database-backed)
  const addComment = useCallback(async (itemId, commentText) => {
    if (!commentText || !commentText.trim()) {
      alert('Comment cannot be empty');
      return null;
    }

    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemId}/comments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ text: commentText.trim() })
        }
      );
      
      if (response.ok) {
        const newComment = await response.json();
        
        // Update local state
        setItemComments(prev => ({
          ...prev,
          [itemId]: [...(prev[itemId] || []), newComment]
        }));
        
        return newComment;
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to add comment');
        return null;
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
      return null;
    }
  }, []);

  // Edit comment
  const editComment = useCallback(async (itemId, commentId, newText) => {
    if (!newText || !newText.trim()) {
      alert('Comment cannot be empty');
      return false;
    }

    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemId}/comments/${commentId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ text: newText.trim() })
        }
      );
      
      if (response.ok) {
        const updatedComment = await response.json();
        
        // Update local state
        setItemComments(prev => ({
          ...prev,
          [itemId]: prev[itemId].map(c => 
            c._id === commentId ? updatedComment : c
          )
        }));
        
        return true;
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to edit comment');
        return false;
      }
    } catch (error) {
      console.error('Error editing comment:', error);
      alert('Failed to edit comment');
      return false;
    }
  }, []);

  // Delete comment
  const deleteComment = useCallback(async (itemId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return false;
    }
    
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemId}/comments/${commentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        // Update local state
        setItemComments(prev => ({
          ...prev,
          [itemId]: (prev[itemId] || []).filter(c => c._id !== commentId)
        }));
        
        return true;
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to delete comment');
        return false;
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
      return false;
    }
  }, []);

  // Get urgency level for an item
  const getUrgencyLevel = useCallback((item) => {
    if (!item.dueDate || item.status === 'Done') return 'none';
    
    const today = startOfDay(new Date());
    const dueDate = startOfDay(new Date(item.dueDate));
    
    if (isBefore(dueDate, today)) return 'overdue';
    
    const daysLeft = differenceInCalendarDays(dueDate, today);
    if (daysLeft === 0) return 'today';
    if (daysLeft === 1) return 'tomorrow';
    if (daysLeft <= 7) return 'week';
    
    return 'future';
  }, []);

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    let filtered = [...teamItems];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // My Assignments filter (takes priority)
    if (showMyAssignments && userProfile?._id) {
      filtered = filtered.filter(item => {
        const assigneeId = item.assignedTo?._id || item.assignedTo;
        return assigneeId === userProfile._id;
      });
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => item.status === filterStatus);
    }

    // Priority filter
    if (filterPriority !== 'all') {
      filtered = filtered.filter(item => item.priority === filterPriority);
    }

    // Assignee filter (only if not using My Assignments)
    if (filterAssignee !== 'all' && !showMyAssignments) {
      if (filterAssignee === 'me') {
        filtered = filtered.filter(item => 
          item.assignedTo?._id === userProfile?._id ||
          item.assignedTo === userProfile?._id
        );
      } else if (filterAssignee === 'unassigned') {
        filtered = filtered.filter(item => !item.assignedTo);
      } else {
        filtered = filtered.filter(item => 
          item.assignedTo?._id === filterAssignee ||
          item.assignedTo === filterAssignee
        );
      }
    }

    // Event filter
    if (filterEvent !== 'all') {
      if (filterEvent === 'none') {
        filtered = filtered.filter(item => !item.event);
      } else {
        filtered = filtered.filter(item => 
          item.event?._id === filterEvent ||
          item.event === filterEvent
        );
      }
    }

    // Due date filter
    if (filterDueDate !== 'all') {
      const today = startOfDay(new Date());
      filtered = filtered.filter(item => {
        if (!item.dueDate) return filterDueDate === 'none';
        
        const dueDate = startOfDay(new Date(item.dueDate));
        const daysLeft = differenceInCalendarDays(dueDate, today);

        switch (filterDueDate) {
          case 'overdue':
            return isBefore(dueDate, today) && item.status !== 'Done';
          case 'today':
            return daysLeft === 0;
          case 'tomorrow':
            return daysLeft === 1;
          case 'week':
            return daysLeft >= 0 && daysLeft <= 7;
          case 'month':
            return daysLeft >= 0 && daysLeft <= 30;
          case 'none':
            return false;
          default:
            return true;
        }
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'dueDate':
          const aDate = a.dueDate ? new Date(a.dueDate) : new Date('2099-12-31');
          const bDate = b.dueDate ? new Date(b.dueDate) : new Date('2099-12-31');
          compareValue = aDate - bDate;
          break;
        case 'priority':
          const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
          compareValue = (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4);
          break;
        case 'status':
          const statusOrder = { 'To Do': 1, 'In Progress': 2, 'Done': 3 };
          compareValue = (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
          break;
        case 'title':
          compareValue = (a.title || '').localeCompare(b.title || '');
          break;
        case 'assignee':
          const aName = a.assignedTo?.firstName || a.assignedTo?.name || 'Unassigned';
          const bName = b.assignedTo?.firstName || b.assignedTo?.name || 'Unassigned';
          compareValue = aName.localeCompare(bName);
          break;
        default:
          compareValue = 0;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  }, [teamItems, searchQuery, filterStatus, filterPriority, filterAssignee, filterEvent, filterDueDate, sortBy, sortOrder, userProfile, showMyAssignments]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const today = startOfDay(new Date());
    
    return {
      total: teamItems.length,
      overdue: teamItems.filter(item => {
        if (!item.dueDate || item.status === 'Done') return false;
        return isBefore(startOfDay(new Date(item.dueDate)), today);
      }).length,
      dueSoon: teamItems.filter(item => {
        if (!item.dueDate || item.status === 'Done') return false;
        const daysLeft = differenceInCalendarDays(startOfDay(new Date(item.dueDate)), today);
        return daysLeft >= 0 && daysLeft <= 7;
      }).length,
      inProgress: teamItems.filter(item => item.status === 'In Progress').length,
      completed: teamItems.filter(item => item.status === 'Done').length,
      todo: teamItems.filter(item => item.status === 'To Do').length
    };
  }, [teamItems]);

  const openModal = () => {
    if (!canManage) {
      alert('You need management permissions to create or edit assignments.');
      return;
    }
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingItem(null);
    setIsModalOpen(false);
  };

  const handleItemAdded = (newItem) => {
    setTeamItems([newItem, ...teamItems]);
    closeModal();
  };

  const handleItemUpdated = (updatedItem) => {
    setTeamItems(
      teamItems.map((item) => (item._id === updatedItem._id ? updatedItem : item))
    );
    closeModal();
    if (quickViewItem?._id === updatedItem._id) {
      setQuickViewItem(updatedItem);
    }
  };

  const handleEdit = (item) => {
    if (!canManage) {
      alert('You need management permissions to edit assignments.');
      return;
    }
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!canManage) {
      alert('You need management permissions to delete assignments.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this assignment?')) {
      try {
        const config = {
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token') && {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            })
          }
        };

        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${id}`,
          config
        );
        
        setTeamItems(prevItems => prevItems.filter((item) => item._id !== id));
        if (quickViewItem?._id === id) {
          setQuickViewItem(null);
        }
        
      } catch (err) {
        console.error('Error deleting assignment:', err);
        
        if (err.response?.status === 403) {
          alert('You don\'t have permission to delete this assignment.');
          return;
        }
        
        if (err.response?.status === 404) {
          setTeamItems(prevItems => prevItems.filter((item) => item._id !== id));
          return;
        }
        
        let errorMessage = 'Failed to delete assignment';
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        }
        
        alert(errorMessage);
      }
    }
  };

  // Quick mark as complete
  const handleMarkComplete = async (item) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('token') && {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          })
        }
      };

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${item._id}`,
        { status: 'Done' },
        config
      );
      
      handleItemUpdated(response.data);
    } catch (err) {
      console.error('Error marking assignment complete:', err);
      alert('Failed to update assignment status.');
    }
  };

  // Bulk actions
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const selectAllFiltered = () => {
    const allIds = new Set(filteredAndSortedItems.map(item => item._id));
    setSelectedItems(allIds);
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
    setBulkActionMode(false);
  };

  const handleBulkDelete = async () => {
    if (!canManage) {
      alert('You need management permissions to delete assignments.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedItems.size} assignment(s)?`)) {
      return;
    }

    const itemsToDelete = Array.from(selectedItems);
    let successCount = 0;

    for (const itemId of itemsToDelete) {
      try {
        const config = {
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token') && {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            })
          }
        };

        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemId}`,
          config
        );
        successCount++;
      } catch (err) {
        console.error(`Error deleting assignment ${itemId}:`, err);
      }
    }

    setTeamItems(prevItems => prevItems.filter(item => !selectedItems.has(item._id)));
    clearSelection();
    
    if (successCount > 0) {
      alert(`Successfully deleted ${successCount} assignment(s).`);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (!canManage) {
      alert('You need management permissions to update assignments.');
      return;
    }

    const itemsToUpdate = Array.from(selectedItems);
    let successCount = 0;

    for (const itemId of itemsToUpdate) {
      try {
        const config = {
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token') && {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            })
          }
        };

        const response = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemId}`,
          { status: newStatus },
          config
        );
        
        setTeamItems(prev => prev.map(item => 
          item._id === itemId ? response.data : item
        ));
        successCount++;
      } catch (err) {
        console.error(`Error updating assignment ${itemId}:`, err);
      }
    }

    clearSelection();
    
    if (successCount > 0) {
      alert(`Successfully updated ${successCount} assignment(s).`);
    }
  };

  // Quick view panel
  const openQuickView = async (item) => {
    setQuickViewItem(item);
    // Fetch comments when opening quick view
    if (item._id) {
      await fetchComments(item._id);
    }
  };

  const closeQuickView = () => {
    setQuickViewItem(null);
  };

  // Create from template
  const handleCreateFromTemplate = (template) => {
    // Don't set as editingItem (which would trigger update mode)
    // Instead, pass template data through a separate mechanism
    setEditingItem({ ...template, isTemplate: true });
    setIsModalOpen(true);
    setIsTemplateModalOpen(false);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterPriority('all');
    setFilterAssignee('all');
    setFilterEvent('all');
    setFilterDueDate('all');
    setShowMyAssignments(false);
    setShowAdvancedFilters(false);
  };

  // Clear advanced filters only
  const clearAdvancedFilters = () => {
    setFilterPriority('all');
    setFilterAssignee('all');
    setFilterEvent('all');
    setFilterDueDate('all');
  };

  // Get unique events and assignees for filters
  const uniqueEvents = useMemo(() => {
    const events = teamItems
      .filter(item => item.event)
      .map(item => item.event);
    const uniqueMap = new Map();
    events.forEach(event => {
      const id = event._id || event;
      const title = event.title || event.name || 'Unknown Event';
      uniqueMap.set(id, { _id: id, title });
    });
    return Array.from(uniqueMap.values());
  }, [teamItems]);

  const uniqueAssignees = useMemo(() => {
    const assignees = teamItems
      .filter(item => item.assignedTo)
      .map(item => item.assignedTo);
    const uniqueMap = new Map();
    assignees.forEach(assignee => {
      const id = assignee._id || assignee;
      const name = assignee.firstName && assignee.lastName
        ? `${assignee.firstName} ${assignee.lastName}`
        : assignee.name || 'Unknown';
      uniqueMap.set(id, { _id: id, name });
    });
    return Array.from(uniqueMap.values());
  }, [teamItems]);

  // Confetti celebration on task completion
  const triggerConfetti = useCallback(() => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 9999
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
        colors: ['#a4eb3f', '#8BC726', '#10b981', '#ffc107', '#ffffff']
      });
    }

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }, []);

  // Override handleMarkComplete to add confetti
  const handleMarkCompleteWithConfetti = async (item) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('token') && {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          })
        }
      };

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${item._id}`,
        { status: 'Done' },
        config
      );
      
      handleItemUpdated(response.data);
      triggerConfetti();
    } catch (err) {
      console.error('Error marking assignment complete:', err);
      alert('Failed to update assignment status.');
    }
  };

  if (loading || permissionsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          {/* Header Skeleton */}
          <div className={styles.pageHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.titleSection}>
                <h1 className={styles.pageTitle}>
                  <FaClipboardList className={styles.titleIcon} /> Assignments
                </h1>
                <span className={styles.itemCount}>Loading...</span>
              </div>
            </div>
          </div>
          
          {/* Skeleton Grid */}
          <div className={styles.itemsGrid}>
            {[...Array(6)].map((_, index) => (
              <TeamItemCardSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.errorState}>
            <FaExclamationTriangle className={styles.errorIcon} />
            <h3>Failed to Load Assignments</h3>
            <p>Something went wrong. Please try refreshing the page.</p>
            <button 
              className={styles.retryBtn}
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = searchQuery || filterStatus !== 'all' || filterPriority !== 'all' || 
                          filterAssignee !== 'all' || filterEvent !== 'all' || filterDueDate !== 'all' || showMyAssignments;
  
  const hasAdvancedFilters = filterPriority !== 'all' || filterAssignee !== 'all' || 
                            filterEvent !== 'all' || filterDueDate !== 'all';

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Stats Banner */}
        {teamItems.length > 0 && (
          <div className={styles.statsBanner}>
            <div className={`${styles.statBlock} ${styles.statOverdue} ${stats.overdue > 0 ? styles.hasItems : ''}`}>
              <div className={styles.statIconWrap}>
                <FaExclamationTriangle />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statNumber}>{stats.overdue}</span>
                <span className={styles.statLabel}>Overdue</span>
              </div>
            </div>
            <div className={styles.statDivider} />
            <div className={`${styles.statBlock} ${styles.statDueSoon}`}>
              <div className={styles.statIconWrap}>
                <FaClock />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statNumber}>{stats.dueSoon}</span>
                <span className={styles.statLabel}>Due Soon</span>
              </div>
            </div>
            <div className={styles.statDivider} />
            <div className={`${styles.statBlock} ${styles.statInProgress}`}>
              <div className={styles.statIconWrap}>
                <FaPlay />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statNumber}>{stats.inProgress}</span>
                <span className={styles.statLabel}>In Progress</span>
              </div>
            </div>
            <div className={styles.statDivider} />
            <div className={`${styles.statBlock} ${styles.statCompleted}`}>
              <div className={styles.statIconWrap}>
                <FaCheckCircle />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statNumber}>{stats.completed}</span>
                <span className={styles.statLabel}>Done</span>
              </div>
            </div>
            <div className={styles.statDivider} />
            <div className={`${styles.statBlock} ${styles.statTotal}`}>
              <div className={styles.statIconWrap}>
                <FaLayerGroup />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statNumber}>{stats.total}</span>
                <span className={styles.statLabel}>Total</span>
              </div>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.titleSection}>
              <h1 className={styles.pageTitle}>
                <FaClipboardList className={styles.titleIcon} /> Assignments
              </h1>
              <span className={styles.itemCount}>
                {filteredAndSortedItems.length} {filteredAndSortedItems.length === 1 ? 'item' : 'items'}
                {hasActiveFilters && ` (filtered from ${teamItems.length})`}
              </span>
            </div>
          </div>
        </div>

        {/* Controls Bar - Filters + View Toggle in One Row */}
        {teamItems.length > 0 && (
          <>
            <div className={styles.controlsBar}>
              {/* Search */}
              <div className={styles.searchBox}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                {searchQuery && (
                  <button
                    className={styles.clearSearchBtn}
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              {/* My Assignments Toggle */}
              <button
                className={`${styles.myAssignmentsBtn} ${showMyAssignments ? styles.myAssignmentsActive : ''}`}
                onClick={() => setShowMyAssignments(!showMyAssignments)}
                title={showMyAssignments ? 'Show all assignments' : 'Show only my assignments'}
              >
                <FaUser /> My Tasks
              </button>

              {/* Status Filter - Always Visible */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Status</option>
                <option value="To Do">To Do</option>
                <option value="In Progress">In Progress</option>
                <option value="Done">Done</option>
              </select>

              {/* Sort Dropdown */}
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [newSortBy, newSortOrder] = e.target.value.split('-');
                  setSortBy(newSortBy);
                  setSortOrder(newSortOrder);
                }}
                className={styles.filterSelect}
              >
                <option value="dueDate-asc">Due Date ↑</option>
                <option value="dueDate-desc">Due Date ↓</option>
                <option value="priority-asc">Priority ↑</option>
                <option value="priority-desc">Priority ↓</option>
                <option value="title-asc">Title A-Z</option>
              </select>

              {/* Advanced Filters Toggle */}
              <button
                className={`${styles.advancedFiltersBtn} ${showAdvancedFilters || hasAdvancedFilters ? styles.advancedFiltersActive : ''}`}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                title="Advanced filters"
              >
                <FaFilter />
                {hasAdvancedFilters && <span className={styles.filterBadge}></span>}
              </button>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  className={styles.clearFiltersBtn}
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  <FaTimes />
                </button>
              )}

              {/* View Toggle */}
              <div className={styles.viewToggle}>
                <button
                  className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid View"
                  aria-label="Grid view"
                >
                  <FaThLarge />
                </button>
                <button
                  className={`${styles.viewBtn} ${viewMode === 'table' ? styles.viewBtnActive : ''}`}
                  onClick={() => setViewMode('table')}
                  title="Table View"
                  aria-label="Table view"
                >
                  <FaTable />
                </button>
                <button
                  className={`${styles.viewBtn} ${viewMode === 'kanban' ? styles.viewBtnActive : ''}`}
                  onClick={() => setViewMode('kanban')}
                  title="Kanban Board"
                  aria-label="Kanban view"
                >
                  <FaTh />
                </button>
                <button
                  className={`${styles.viewBtn} ${viewMode === 'calendar' ? styles.viewBtnActive : ''}`}
                  onClick={() => setViewMode('calendar')}
                  title="Calendar View"
                  aria-label="Calendar view"
                >
                  <FaCalendar />
                </button>
              </div>
            </div>

            {/* Advanced Filters Panel - Collapsible */}
            {showAdvancedFilters && (
              <div className={styles.advancedFiltersPanel}>
                <div className={styles.advancedFiltersHeader}>
                  <span className={styles.advancedFiltersTitle}>
                    <FaFilter /> Advanced Filters
                  </span>
                  {hasAdvancedFilters && (
                    <button
                      className={styles.clearAdvancedBtn}
                      onClick={clearAdvancedFilters}
                    >
                      Clear
                    </button>
                  )}
                </div>
                
                <div className={styles.advancedFiltersGrid}>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Priority</label>
                    <select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      className={styles.filterSelectAdvanced}
                    >
                      <option value="all">All</option>
                      <option value="High">🔴 High</option>
                      <option value="Medium">🟡 Medium</option>
                      <option value="Low">🟢 Low</option>
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Assignee</label>
                    <select
                      value={filterAssignee}
                      onChange={(e) => setFilterAssignee(e.target.value)}
                      className={styles.filterSelectAdvanced}
                      disabled={showMyAssignments}
                    >
                      <option value="all">All</option>
                      <option value="unassigned">Unassigned</option>
                      {uniqueAssignees.map(assignee => (
                        <option key={assignee._id} value={assignee._id}>
                          {assignee.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Due Date</label>
                    <select
                      value={filterDueDate}
                      onChange={(e) => setFilterDueDate(e.target.value)}
                      className={styles.filterSelectAdvanced}
                    >
                      <option value="all">All</option>
                      <option value="overdue">Overdue</option>
                      <option value="today">Today</option>
                      <option value="tomorrow">Tomorrow</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="none">No Due Date</option>
                    </select>
                  </div>

                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Event</label>
                    <select
                      value={filterEvent}
                      onChange={(e) => setFilterEvent(e.target.value)}
                      className={styles.filterSelectAdvanced}
                    >
                      <option value="all">All Events</option>
                      <option value="none">No Event</option>
                      {uniqueEvents.map(event => (
                        <option key={event._id} value={event._id}>
                          {event.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Bulk Actions Bar */}
        {bulkActionMode && selectedItems.size > 0 && canManage && (
          <div className={styles.bulkActionsBar}>
            <span className={styles.bulkActionLabel}>
              {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
            </span>
            <div className={styles.bulkActionButtons}>
              <button
                className={styles.bulkActionBtn}
                onClick={() => handleBulkStatusChange('To Do')}
              >
                Mark as To Do
              </button>
              <button
                className={styles.bulkActionBtn}
                onClick={() => handleBulkStatusChange('In Progress')}
              >
                Mark as In Progress
              </button>
              <button
                className={styles.bulkActionBtn}
                onClick={() => handleBulkStatusChange('Done')}
              >
                Mark as Done
              </button>
              <button
                className={`${styles.bulkActionBtn} ${styles.bulkActionDanger}`}
                onClick={handleBulkDelete}
              >
                <FaTrash /> Delete
              </button>
              <button
                className={styles.bulkActionBtn}
                onClick={clearSelection}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        {teamItems.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>
              <FaClipboardList className={styles.emptyIcon} />
            </div>
            <h3 className={styles.emptyTitle}>No Assignments Yet</h3>
            <p className={styles.emptyText}>
              Create your first team assignment to track tasks, duties, and deadlines for your team members.
            </p>
            {canManage && (
              <button className={styles.emptyActionBtn} onClick={openModal}>
                <FaPlus /> Create First Assignment
              </button>
            )}
          </div>
        ) : filteredAndSortedItems.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>
              <FaSearch className={styles.emptyIcon} />
            </div>
            <h3 className={styles.emptyTitle}>No Results Found</h3>
            <p className={styles.emptyText}>
              Try adjusting your search or filters to find what you're looking for.
            </p>
            <button className={styles.emptyActionBtn} onClick={clearAllFilters}>
              <FaTimes /> Clear Filters
            </button>
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className={styles.itemsGrid}>
                {filteredAndSortedItems.map((item) => {
                  // Check if current user is assigned to this item
                  const isAssignedUser = item.assignedTo && userProfile && 
                    (item.assignedTo._id === userProfile._id || item.assignedTo === userProfile._id);
                  
                  return (
                    <TeamItemCard
                      key={item._id}
                      item={item}
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item._id)}
                      onMarkComplete={() => handleMarkCompleteWithConfetti(item)}
                      onQuickView={() => openQuickView(item)}
                      canManage={canManage}
                      canComplete={isAssignedUser} // Allow assigned users to complete their tasks
                      isHighlighted={highlightItemId === item._id}
                      isSelected={selectedItems.has(item._id)}
                      onToggleSelect={() => toggleItemSelection(item._id)}
                      bulkMode={bulkActionMode}
                      urgencyLevel={getUrgencyLevel(item)}
                      commentCount={(itemComments[item._id] || []).length}
                    />
                  );
                })}
              </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
              <TeamItemTable
                teamItems={filteredAndSortedItems}
                programId={programId}
                onItemUpdated={handleItemUpdated}
                onItemDeleted={handleDelete}
                onItemAdded={handleItemAdded}
                onEditItem={handleEdit}
                onQuickView={openQuickView}
                canManage={canManage}
                currentUserId={userProfile?._id}
                selectedItems={selectedItems}
                onToggleSelect={toggleItemSelection}
                bulkMode={bulkActionMode}
                onSelectAll={selectAllFiltered}
                onSort={(column) => {
                  if (sortBy === column) {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(column);
                    setSortOrder('asc');
                  }
                }}
                currentSort={sortBy}
                currentSortOrder={sortOrder}
              />
            )}

            {/* Kanban View */}
            {viewMode === 'kanban' && (
              <KanbanView
                items={filteredAndSortedItems}
                onItemUpdated={handleItemUpdated}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onQuickView={openQuickView}
                canManage={canManage}
              />
            )}

            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <CalendarView
                items={filteredAndSortedItems}
                onEdit={handleEdit}
                onQuickView={openQuickView}
                canManage={canManage}
              />
            )}
          </>
        )}

        {/* Floating Action Buttons Stack */}
        <div className={styles.floatingButtonsStack}>
          {/* Bulk Mode Toggle */}
          {canManage && teamItems.length > 0 && !bulkActionMode && (
            <button
              className={styles.floatingSecondaryBtn}
              onClick={() => setBulkActionMode(true)}
              title="Select Multiple"
              aria-label="Select Multiple"
            >
              <FaCheckSquare />
              <span className={styles.floatingBtnTooltip}>Bulk Select</span>
            </button>
          )}

          {/* Templates Button */}
          {canManage && (
            <button
              className={`${styles.floatingSecondaryBtn} ${styles.templatesBtn}`}
              onClick={() => setIsTemplateModalOpen(true)}
              title="Use Template"
              aria-label="Use Template"
            >
              <FaStar />
              <span className={styles.floatingBtnTooltip}>Templates</span>
            </button>
          )}

          {/* Main Add Button */}
          {canManage && (
            <button
              className={styles.floatingAddButton}
              onClick={openModal}
              title="Create New Assignment"
              aria-label="Create New Assignment"
            >
              <FaPlus />
            </button>
          )}
        </div>

        {/* Create/Edit Modal */}
        {isModalOpen && (
          <TeamItemModal
            isOpen={isModalOpen}
            onClose={closeModal}
            programId={programId}
            itemToEdit={editingItem}
            onItemAdded={handleItemAdded}
            onItemUpdated={handleItemUpdated}
          />
        )}

        {/* Templates Modal */}
        {isTemplateModalOpen && (
          <TaskTemplatesModal
            isOpen={isTemplateModalOpen}
            onClose={() => setIsTemplateModalOpen(false)}
            onSelectTemplate={handleCreateFromTemplate}
          />
        )}

        {/* Quick View Panel */}
        {quickViewItem && (() => {
          // Check if current user is assigned to this item
          const isAssignedUser = quickViewItem.assignedTo && userProfile && 
            (quickViewItem.assignedTo._id === userProfile._id || quickViewItem.assignedTo === userProfile._id);
          
          return (
            <QuickViewPanel
              item={quickViewItem}
              onClose={closeQuickView}
              onEdit={() => {
                handleEdit(quickViewItem);
                closeQuickView();
              }}
              onDelete={() => {
                handleDelete(quickViewItem._id);
                closeQuickView();
              }}
              onMarkComplete={() => handleMarkComplete(quickViewItem)}
              onStatusChange={handleItemUpdated}
              canManage={canManage}
              canComplete={isAssignedUser} // Allow assigned users to complete their tasks
              comments={itemComments[quickViewItem._id] || []}
              onAddComment={(comment) => addComment(quickViewItem._id, comment)}
              onEditComment={(commentId, newText) => editComment(quickViewItem._id, commentId, newText)}
              onDeleteComment={(commentId) => deleteComment(quickViewItem._id, commentId)}
              currentUserId={userProfile?._id}
            />
          );
        })()}
      </div>
    </div>
  );
};

export default TeamItemsPage;
